import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Mail, ExternalLink, Users, X, Copy, Briefcase } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";

export default function PlannerDashboard() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState({ weddings: [], pending_invites: [] });
  const [mode, setMode] = useState(null); // 'create' | 'invite'
  const [busy, setBusy] = useState(false);
  const [createForm, setCreateForm] = useState({ couple_email: "", partner_one: "", partner_two: "", wedding_date: "", venue: "", city: "", template: "luxury-black-tie" });
  const [inviteForm, setInviteForm] = useState({ couple_email: "", couple_names: "", wedding_date: "", note: "" });

  useEffect(() => {
    if (!loading && (!user || (user.role !== "planner" && user.role !== "admin"))) nav("/login", { replace: true });
  }, [user, loading, nav]);

  async function load() {
    const { data } = await api.get("/planner/clients");
    setData(data);
  }
  useEffect(() => { if (user) load().catch(() => {}); }, [user]);

  async function createClient(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/planner/clients", createForm);
      toast.success("Wedding site created for client");
      setMode(null);
      setCreateForm({ couple_email: "", partner_one: "", partner_two: "", wedding_date: "", venue: "", city: "", template: "luxury-black-tie" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  async function sendInvite(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/planner/invites", inviteForm);
      toast.success("Invite sent (link logged to backend console)");
      setMode(null);
      setInviteForm({ couple_email: "", couple_names: "", wedding_date: "", note: "" });
      load();
      const fullLink = `${window.location.origin}${data.invite_link}`;
      navigator.clipboard?.writeText(fullLink).catch(() => {});
      toast(`Invite link copied: ${fullLink}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }

  if (!user) return null;

  return (
    <div className="bg-[var(--bg)] min-h-screen">
      <Navbar />
      <div className="container-wide px-6 md:px-12 py-12">
        <div className="flex items-center gap-3 mb-2"><Briefcase className="w-5 h-5 text-[var(--gold)]" /><p className="text-[11px] uppercase tracking-[0.22em] text-[var(--gold)]">Planner Console</p></div>
        <h1 className="font-serif font-light text-5xl mb-2">Your Clients</h1>
        <p className="text-[var(--muted)] mb-10">Create branded wedding sites for couples directly, or invite them to claim and manage their own.</p>

        <div className="flex flex-wrap gap-3 mb-8">
          <button data-testid="planner-create-btn" onClick={() => setMode("create")} className="btn-gold"><Plus className="w-4 h-4" /> New Client (Direct)</button>
          <button data-testid="planner-invite-btn" onClick={() => setMode("invite")} className="btn-outline-gold"><Mail className="w-4 h-4" /> Invite via Email</button>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data.weddings.length === 0 && data.pending_invites.length === 0 && (
            <div className="col-span-full bg-white border border-[var(--line)] rounded-2xl p-12 text-center">
              <Users className="w-10 h-10 mx-auto text-[var(--gold)] mb-4" />
              <p className="font-serif text-2xl mb-2">No clients yet</p>
              <p className="text-[var(--muted)]">Create or invite your first couple to get started.</p>
            </div>
          )}
          {data.weddings.map((w) => (
            <div key={w.wedding_id} className="bg-white border border-[var(--line)] rounded-2xl p-6">
              <p className="font-script text-2xl text-[var(--gold)]">{w.couple_names}</p>
              {w.wedding_date && <p className="text-xs uppercase tracking-[0.18em] text-[var(--muted)] mt-1">{new Date(w.wedding_date).toLocaleDateString()}</p>}
              <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                <div><span className="text-[var(--muted)]">Guests:</span> <span className="font-medium">{w.guest_count}</span></div>
                <div><span className="text-[var(--muted)]">Memories:</span> <span className="font-medium">{w.memory_count}</span></div>
              </div>
              <Link to={`/site/${w.slug}`} target="_blank" className="text-xs text-[var(--gold)] flex items-center gap-1 mt-4">View site <ExternalLink className="w-3 h-3" /></Link>
            </div>
          ))}
          {data.pending_invites.map((inv) => (
            <div key={inv.invite_id} className="bg-[var(--bg-warm)] rounded-2xl p-6">
              <p className="text-[10px] uppercase tracking-wider text-[var(--gold)] mb-2">Pending invite</p>
              <p className="font-serif text-xl">{inv.couple_names}</p>
              <p className="text-sm text-[var(--muted)] mt-1">{inv.couple_email}</p>
              <div className="mt-4 flex gap-2">
                <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/invite/${inv.token}`); toast.success("Link copied"); }} className="text-xs text-[var(--gold)] flex items-center gap-1"><Copy className="w-3 h-3" /> Copy invite link</button>
              </div>
            </div>
          ))}
        </div>

        {mode === "create" && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl p-8 w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between mb-6"><h3 className="font-serif text-2xl">New Client (Direct)</h3><button onClick={() => setMode(null)}><X className="w-5 h-5" /></button></div>
              <form onSubmit={createClient} className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Couple email</label><input data-testid="pl-email" type="email" required value={createForm.couple_email} onChange={(e) => setCreateForm({...createForm, couple_email: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Partner One</label><input required value={createForm.partner_one} onChange={(e) => setCreateForm({...createForm, partner_one: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Partner Two</label><input required value={createForm.partner_two} onChange={(e) => setCreateForm({...createForm, partner_two: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Wedding Date</label><input type="date" value={createForm.wedding_date} onChange={(e) => setCreateForm({...createForm, wedding_date: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">City</label><input value={createForm.city} onChange={(e) => setCreateForm({...createForm, city: e.target.value})} className="input-luxe" /></div>
                <div className="col-span-2"><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Venue</label><input value={createForm.venue} onChange={(e) => setCreateForm({...createForm, venue: e.target.value})} className="input-luxe" /></div>
                <button type="submit" disabled={busy} data-testid="pl-submit" className="btn-gold col-span-2">{busy ? "Creating…" : "Create Wedding Site"}</button>
              </form>
            </div>
          </div>
        )}
        {mode === "invite" && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
            <div className="bg-white rounded-2xl p-8 w-full max-w-md">
              <div className="flex justify-between mb-6"><h3 className="font-serif text-2xl">Invite Couple</h3><button onClick={() => setMode(null)}><X className="w-5 h-5" /></button></div>
              <form onSubmit={sendInvite} className="space-y-4">
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Couple email</label><input type="email" required value={inviteForm.couple_email} onChange={(e) => setInviteForm({...inviteForm, couple_email: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Couple names</label><input required value={inviteForm.couple_names} onChange={(e) => setInviteForm({...inviteForm, couple_names: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Wedding date (optional)</label><input type="date" value={inviteForm.wedding_date} onChange={(e) => setInviteForm({...inviteForm, wedding_date: e.target.value})} className="input-luxe" /></div>
                <div><label className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]">Personal note</label><textarea rows={3} value={inviteForm.note} onChange={(e) => setInviteForm({...inviteForm, note: e.target.value})} className="input-luxe resize-none" /></div>
                <button type="submit" disabled={busy} className="btn-gold w-full">{busy ? "Sending…" : "Send Invite"}</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
